import { Box, Button, Typography } from "@mui/material";
import { WriteLockTooltip } from "@/shared/components/WriteLockTooltip";
import type { CampaignData, FurnaceBillet } from "../models/CampaignData";
import { FurnaceBilletsBox } from "./FurnaceBilletsBox";

// Two header lines: everything on the first, the current billet under the next one
// The second line is shorter so the current billet sits close under the next one
const HEADER_ROWS = "32px 18px";

interface CampaignHeaderProps {
	campaign: CampaignData | null;
	furnaceBillets: FurnaceBillet[];
	productName: string;
	recipeName: string;
	onChangeProduct?: () => void;
	onChangeWorkOrder?: () => void;
	onOpenLastStop?: () => void;
	writeDisabled?: boolean;
}

export const CampaignHeader = ({
	campaign,
	furnaceBillets,
	productName,
	recipeName,
	onChangeProduct,
	onChangeWorkOrder,
	onOpenLastStop,
	writeDisabled = false,
}: CampaignHeaderProps) => {
	const hasDelay = campaign?.LastDelayId != null;

	return (
		<Box
			sx={{
				display: "grid",
				gridTemplateColumns: "1fr auto 1fr",
				gridTemplateRows: HEADER_ROWS,
				columnGap: 2,
				alignItems: "center",
				px: 2,
				pt: 0.5,
				// Line 1 centers 18 px of text in 32 px (7 px over it); the same space under the current line
				pb: "11px",
			}}
		>
			<Box
				sx={{
					gridRow: 1,
					gridColumn: 1,
					display: "flex",
					alignItems: "center",
					gap: 2,
					minWidth: 0,
				}}
			>
				{/* Product */}
				<Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
					<Typography sx={{ fontSize: 13, color: "#888" }}>Product:</Typography>
					<Typography
						sx={{ fontSize: 13, fontWeight: "bold", maxWidth: 250 }}
						noWrap
					>
						{productName || "N/A"}
					</Typography>
				</Box>

				{/* Recipe */}
				<Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
					<Typography sx={{ fontSize: 13, color: "#888" }}>Recipe:</Typography>
					<Typography
						sx={{ fontSize: 13, fontWeight: "bold", maxWidth: 180 }}
						noWrap
					>
						{recipeName || "N/A"}
					</Typography>
				</Box>

				{/* Change Product/Recipe Button */}
				<WriteLockTooltip locked={writeDisabled}>
					<Button
						size="small"
						variant="contained"
						onClick={onChangeProduct}
						disabled={writeDisabled}
						sx={{
							fontSize: 12,
							py: 0.25,
							px: 2,
							minWidth: "auto",
							textTransform: "none",
						}}
					>
						Change
					</Button>
				</WriteLockTooltip>

				{/* Last Stop Button */}
				<Button
					size="small"
					variant="contained"
					color="primary"
					onClick={onOpenLastStop}
					disabled={!hasDelay}
					sx={{
						fontSize: 12,
						py: 0.25,
						px: 2,
						minWidth: "auto",
						textTransform: "none",
					}}
				>
					Last Stop
				</Button>
			</Box>

			<Box
				sx={{
					gridRow: "1 / 3",
					gridColumn: 2,
					px: 2,
					// Rules run the full height of the header: out past its padding, content kept in place
					mt: "-4px",
					pt: "4px",
					mb: "-11px",
					pb: "11px",
					alignSelf: "stretch",
					borderLeft: 1,
					borderRight: 1,
					borderColor: "divider",
				}}
			>
				<FurnaceBilletsBox billets={furnaceBillets} rows={HEADER_ROWS} />
			</Box>

			<Box
				sx={{
					gridRow: 1,
					gridColumn: 3,
					display: "flex",
					alignItems: "center",
					justifyContent: "flex-end",
					gap: 2,
				}}
			>
				{/* Work Order */}
				<Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
					<Typography sx={{ fontSize: 13, color: "#888" }}>
						Work Order:
					</Typography>
					<Typography
						sx={{ fontSize: 13, fontWeight: "bold", maxWidth: 180 }}
						noWrap
					>
						{campaign?.WorkOrderName || "N/A"}
					</Typography>
				</Box>

				{/* Change Work Order Button */}
				<WriteLockTooltip locked={writeDisabled}>
					<Button
						size="small"
						variant="contained"
						onClick={onChangeWorkOrder}
						disabled={writeDisabled}
						sx={{
							fontSize: 12,
							py: 0.25,
							px: 2,
							minWidth: "auto",
							textTransform: "none",
						}}
					>
						Change
					</Button>
				</WriteLockTooltip>
			</Box>
		</Box>
	);
};
